import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

/**
 * Surfaces.
 *
 * Depth in this interface comes from a border and a surface colour, not from a
 * shadow. `Card` is the only container primitive; a page that wants a panel
 * composes `Card` rather than inventing another bordered div.
 */

const cardVariants = cva('rounded-lg bg-surface', {
  variants: {
    border: { true: 'border border-line', false: '' },
    elevation: { none: '', xs: 'shadow-xs', sm: 'shadow-sm', md: 'shadow-md' },
    padding: { none: '', sm: 'p-3', md: 'p-4', lg: 'p-5' },
  },
  defaultVariants: { border: true, elevation: 'none', padding: 'md' },
});

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, border, elevation, padding, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(cardVariants({ border, elevation, padding }), className)}
      {...props}
    />
  );
});

/** Card header with an optional action slot. Keeps title/action alignment identical everywhere. */
export function CardHeader({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3', className)}>
      <div className="flex min-w-0 items-start gap-2.5">
        {icon ? <div className="mt-0.5 shrink-0 text-ink-muted">{icon}</div> : null}
        <div className="min-w-0">
          <h3 className="truncate text-md font-semibold text-ink">{title}</h3>
          {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function Divider({
  className,
  orientation = 'horizontal',
}: {
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}) {
  return (
    <div
      role="separator"
      aria-orientation={orientation}
      className={cn(
        'bg-line',
        orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
        className,
      )}
    />
  );
}

/**
 * A labelled fact.
 *
 * The label never truncates — it names the value, and clipping it destroys the
 * only clue to what the value is. Long values wrap instead.
 */
/** A bare email address: no spaces, one @. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Field({
  label,
  value,
  hint,
  className,
  masked = false,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  className?: string;
  /** Renders in a monospace face — for reference numbers and masked identifiers. */
  masked?: boolean;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-caption font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd
        className={cn(
          'mt-1 break-words text-base text-ink',
          masked && 'font-mono text-sm tracking-tight',
        )}
      >
        {value === null || value === undefined || value === '' ? (
          <span className="text-ink-subtle">—</span>
        ) : typeof value === 'string' && EMAIL.test(value) ? (
          /*
           * An address is one word: wrapped, it split mid-word ("…example.co"
           * over "m"). It stays on one line, cut with an ellipsis where the
           * column is narrow, and reads whole on hover.
           */
          <span className="block truncate" title={value}>
            {value}
          </span>
        ) : (
          value
        )}
      </dd>
      {hint ? <p className="mt-0.5 text-caption text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/** A responsive grid of `Field`s. Two columns on a laptop, one on a tablet. */
export function FieldGrid({
  children,
  columns = 2,
  className,
}: {
  children: ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-4',
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-1 tl:grid-cols-2',
        columns === 3 && 'grid-cols-1 tp:grid-cols-2 lap:grid-cols-3',
        className,
      )}
    >
      {children}
    </dl>
  );
}
